import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Gift, Heart, Play, Pause, ListMusic } from "lucide-react";
import { toast, Toaster } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useMusic } from "../audio/MusicContext";
import { Reveal, Floral, GoldDivider, Marquee, SectionHeading } from "../components/Decor";
import Countdown from "../components/Countdown";
import { COUPLE, SHARE_TEXT, WELCOME, MESSAGES } from "../constants/site";

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.25 + i * 0.18, duration: 1.05, ease: [0.16, 1, 0.3, 1] },
  }),
};

function FeaturedPlayer() {
  const { featured, playlist, play, playTrack, toggle, current, playing } = useMusic();
  if (!featured) return null;

  return (
    <section className="relative mx-auto max-w-4xl px-5 py-20 sm:py-24">
      <Floral variant="side" className="absolute -left-6 top-8 hidden w-28 opacity-70 lg:block" />
      <Reveal>
        <div className="paper-card gold-frame relative px-6 py-12 text-center sm:px-12" data-testid="featured-music">
          <p className="eyebrow">Nossa música</p>
          <h2 className="mt-4 font-serif text-3xl italic text-ink sm:text-4xl">
            Dê o play e ouça a nossa música
          </h2>
          <p className="mx-auto mt-3 max-w-xl font-sans text-sm font-light leading-relaxed text-muted">
            composta pelo Rodrigo para a Talita, inspirada na nossa linda história ❤️
          </p>
          <button
            data-testid="audio-player-play-btn"
            onClick={current === 0 ? toggle : () => play(0)}
            aria-label={current === 0 && playing ? "Pausar nossa música" : "Tocar nossa música"}
            className="group mx-auto mt-8 grid h-20 w-20 place-items-center rounded-full border border-gold bg-ink text-paper shadow-soft transition-all duration-500 hover:scale-105 hover:bg-sage-dark"
          >
            {current === 0 && playing ? <Pause size={26} /> : <Play size={26} className="ml-1" />}
          </button>
          <p className="mt-5 font-script text-3xl text-ink">{featured.title}</p>
          <p className="mt-1 font-sans text-[10px] uppercase tracking-[0.28em] text-sage-dark">{featured.artist}</p>
          {featured.note ? <p className="mx-auto mt-4 max-w-md font-serif text-lg italic text-muted">{featured.note}</p> : null}

          {playlist.length > 0 && (
            <div className="mx-auto mt-9 max-w-md border-t border-gold-border pt-6">
              <p className="flex items-center justify-center gap-2 font-sans text-[10px] uppercase tracking-[0.3em] text-sage-dark">
                <ListMusic size={13} /> A nossa playlist
              </p>
              <ul className="mt-4 space-y-1">
                {playlist.map((t, i) => {
                  const active = current === i + 1;
                  return (
                    <li key={t.url}>
                      <button
                        data-testid={`playlist-item-${i}`}
                        onClick={() => playTrack(i + 1)}
                        className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-sage-light ${
                          active ? "bg-sage-light" : ""
                        }`}
                      >
                        {active && playing ? <Pause size={14} className="shrink-0 text-sage-dark" /> : <Play size={14} className="shrink-0 text-sage-dark" />}
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-serif text-lg text-ink">{t.title}</span>
                          <span className="block font-sans text-[10px] uppercase tracking-[0.2em] text-muted">{t.artist}</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </Reveal>
    </section>
  );
}

function MessagesSection() {
  const [messages, setMessages] = useState([]);
  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);

  const load = () =>
    api
      .get("/messages")
      .then(({ data }) => setMessages(data.messages || []))
      .catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await api.post("/messages", { user_name: name, content });
      setName("");
      setContent("");
      toast.success("Mensagem enviada! Após a aprovação do casal, ela aparecerá aqui.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="relative mx-auto max-w-4xl px-5 pb-24">
      <Reveal>
        <SectionHeading eyebrow="Recados" title={MESSAGES.title} />
        <p className="mx-auto mt-4 max-w-xl text-center font-sans text-sm font-light text-muted">{MESSAGES.subtitle}</p>
      </Reveal>

      <div className="mx-auto mt-12 grid max-w-4xl gap-6 md:grid-cols-2">
        {messages.map((m, i) => (
          <Reveal key={m.id} delay={0.06 * i}>
            <article className="paper-card flex h-full flex-col p-7 text-left" data-testid="message-card">
              <p className="flex-1 whitespace-pre-line font-serif text-lg italic leading-relaxed text-ink">“{m.content}”</p>
              <p className="mt-5 font-sans text-[11px] uppercase tracking-[0.24em] text-sage-dark">
                {m.user_name} · {m.date_label}
              </p>
              {m.reply && m.reply.length > 0 && (
                <div className="mt-4 border-l-2 border-gold bg-paperdeep p-4">
                  <p className="whitespace-pre-line font-sans text-sm font-light leading-relaxed text-ink/80">{m.reply[0].content}</p>
                  <p className="mt-3 font-sans text-[10px] uppercase tracking-[0.24em] text-gold-deep">
                    {m.reply[0].user} · {m.reply[0].date_label}
                  </p>
                </div>
              )}
            </article>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <form onSubmit={submit} className="paper-card gold-frame mx-auto mt-12 max-w-2xl p-8 text-left" data-testid="message-form">
          <h3 className="text-center font-script text-3xl text-ink">Envie sua mensagem</h3>
          <div className="mt-6 space-y-4">
            <input
              data-testid="message-input-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              minLength={2}
              maxLength={60}
              placeholder="Seu nome"
              className="w-full border border-gold-border bg-white px-4 py-3 font-sans text-sm outline-none transition-colors focus:border-sage"
            />
            <textarea
              data-testid="message-input-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              required
              minLength={2}
              maxLength={1000}
              rows={4}
              placeholder="Escreva um recado de carinho para os noivos"
              className="w-full resize-none border border-gold-border bg-white px-4 py-3 font-sans text-sm outline-none transition-colors focus:border-sage"
            />
            <button type="submit" data-testid="message-submit-btn" disabled={sending} className="btn-gold w-full">
              {sending ? "Enviando..." : "Enviar mensagem"}
            </button>
          </div>
        </form>
      </Reveal>
      <Toaster position="top-center" richColors />
    </section>
  );
}

export default function Inicio() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 40]);

  return (
    <>
      <section ref={heroRef} className="relative flex min-h-[100svh] items-center overflow-hidden" data-testid="hero">
        <Floral variant="top" mirrored className="absolute -right-24 -top-8 w-[380px] opacity-90 sm:w-[460px]" />
        <div className="mx-auto grid w-full max-w-6xl items-center gap-14 px-5 pb-20 pt-28 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10">
          <motion.div style={{ y: textY }} className="relative z-10 text-center lg:text-left">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="font-sans text-[10px] font-medium uppercase tracking-[0.2em] text-gold-deep sm:text-[11px] sm:tracking-[0.34em]"
            >
              {COUPLE.dateLabel}
            </motion.p>
            <h1 className="mt-6 font-script leading-[0.95] text-ink">
              <span className="block overflow-hidden pb-2">
                <motion.span custom={0} variants={lineReveal} initial="hidden" animate="show" className="block text-6xl sm:text-7xl lg:text-8xl">
                  Rodrigo
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-3">
                <motion.span custom={1} variants={lineReveal} initial="hidden" animate="show" className="block text-6xl sm:text-7xl lg:text-8xl">
                  &amp; Talita
                </motion.span>
              </span>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.75 }}
              className="mx-auto mt-6 max-w-md font-serif text-xl italic leading-relaxed text-ink/75 lg:mx-0"
            >
              {SHARE_TEXT}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9 }}
              className="mt-10"
            >
              <p className="mb-3 font-sans text-[10px] uppercase tracking-[0.3em] text-sage-dark">
                Contagem regressiva para o grande dia
              </p>
              <Countdown />
              <div className="mt-8 flex flex-wrap justify-center gap-3 lg:justify-start">
                <Link to="/confirmacao-presenca" data-testid="hero-cta-rsvp" className="btn-gold">
                  <Heart size={14} /> Confirmar presença
                </Link>
                <Link to="/lista-de-presentes" data-testid="hero-cta-gifts" className="btn-outline">
                  <Gift size={14} /> Lista de presentes
                </Link>
              </div>
            </motion.div>
          </motion.div>

          <motion.div style={{ y: photoY }} className="relative z-10 mx-auto w-full max-w-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="gold-frame relative bg-white p-3 shadow-soft"
              data-testid="hero-photo-frame"
            >
              <img
                src="/images/hero-casal.jpg"
                alt="Rodrigo e Talita"
                className="aspect-[3/4] w-full object-cover"
              />
              <p className="py-3 text-center font-sans text-[10px] uppercase tracking-[0.3em] text-sage-dark">
                09 . 01 . 2027 — Dom Guilherme Eventos
              </p>
            </motion.div>
            <Floral variant="bottom" className="absolute -bottom-20 -left-24 -z-0 w-72 opacity-95 sm:w-80" />
          </motion.div>
        </div>
      </section>

      <Marquee items={["Rodrigo & Talita", "09 . 01 . 2027", "Dom Guilherme Eventos", "Almirante Tamandaré — PR"]} />

      <section className="relative mx-auto max-w-3xl px-5 py-20 sm:py-24" data-testid="welcome">
        <Reveal>
          <GoldDivider className="mb-10" />
          <h2 className="text-center font-script text-5xl text-ink sm:text-6xl">{WELCOME.title}</h2>
          <div className="mt-10 space-y-5 text-center">
            <p className="font-serif text-xl italic text-ink">{WELCOME.paragraphs[0]}</p>
            <p className="font-serif text-xl italic text-ink">{WELCOME.paragraphs[1]}</p>
            {WELCOME.paragraphs.slice(2).map((p, i) => (
              <p key={i} className="mx-auto max-w-2xl font-sans text-sm font-light leading-loose text-ink/80 sm:text-base">
                {p}
              </p>
            ))}
            <p className="pt-4 font-sans text-xs uppercase tracking-[0.3em] text-muted">{WELCOME.signature}</p>
            <p className="font-script text-4xl text-sage-dark">{WELCOME.names}</p>
          </div>
        </Reveal>
      </section>

      <FeaturedPlayer />
      <MessagesSection />
    </>
  );
}
